import { API_URLS } from '@/utils/constant';
import { getData, keysToCamel, mathFloor, postData } from '@/utils/helpers';
import { IJackpotPastWinner, IJackpotTicket, IJackpot, IJackpotTicketData, IPagination } from '@/utils/index.model';
import { filter, sortBy, isEmpty, uniqBy, unionBy } from 'lodash';

interface IJackpotTicketState {
  ownerTickets: IJackpotTicket[];
  ownerTicketsLog: IJackpotTicket[];
  ownnerCurrentLuckyDrawTickets: IJackpotTicket[];
  ticketPagination: IPagination;
  ticketLoading: boolean;
  pastWinners: IJackpotPastWinner[];
  pastOwnerTickets: IJackpotTicket[];
  jackpots: IJackpot[];
  currentLuckyDrawTickets: IJackpotTicketData[];
}

const JACKPOT_DATA: IJackpotTicketState = {
  ownerTickets: [],
  ownerTicketsLog: [],
  ownnerCurrentLuckyDrawTickets: [],
  ticketLoading: false,
  pastWinners: [],
  pastOwnerTickets: [],
  jackpots: [],
  currentLuckyDrawTickets: [],
  ticketPagination: { page: 1, pageSize: 20 },
};

export const state = () => JACKPOT_DATA;

export const mutations = {
  INSERT_LUCKY_DRAW_TICKETS(stateData: IJackpotTicketState, tickets: IJackpotTicketData[]) {
    stateData.currentLuckyDrawTickets = [...tickets];
  },
  INSERT_OWNER_TICKETS(stateData: IJackpotTicketState, tickets: IJackpotTicket[]) {
    stateData.ownerTickets = [...tickets];
  },
  INSERT_OWNER_CURRENT_TICKETS(stateData: IJackpotTicketState, tickets: IJackpotTicket[]) {
    stateData.ownnerCurrentLuckyDrawTickets = [...tickets];
  },
  INSERT_OWNER_TICKETS_LOG(stateData: IJackpotTicketState, tickets: IJackpotTicket[]) {
    stateData.ownerTicketsLog = [...tickets];
  },
  UPDATE_OWNER_TICKETS_LOG(stateData: IJackpotTicketState, tickets: IJackpotTicket[]) {
    stateData.ownerTicketsLog = [...stateData.ownerTicketsLog, ...tickets];
  },
  UPDATE_PAGINATION(stateData: IJackpotTicketState, pagination: IPagination): void {
    stateData.ticketPagination = { ...pagination };
  },
  RESET_PAGINATION(stateData: IJackpotTicketState) {
    stateData.ticketPagination = { page: 1, pageSize: 20 };
  },
  INSERT_OWNER_PAST_TICKETS(stateData: IJackpotTicketState, tickets: IJackpotTicket[]) {
    stateData.pastOwnerTickets = [...tickets];
  },
  RESET_OWNER_PAST_TICKETS(stateData: IJackpotTicketState) {
    stateData.pastOwnerTickets = [];
  },
  RESET_OWNER_TICKETS(stateData: IJackpotTicketState) {
    stateData.ownerTickets = [];
  },
  INSERT_PAST_WINNERS(stateData: IJackpotTicketState, tickets: IJackpotPastWinner[]) {
    stateData.pastWinners = [...stateData.pastWinners, ...tickets];
  },
  RESET_PAST_WINNERS(stateData: IJackpotTicketState) {
    stateData.pastWinners = [];
  },
  UPDATE_LOADING(stateData: IJackpotTicketState, loading: boolean): void {
    stateData.ticketLoading = loading;
  },
  INSERT_JACKPOTS(stateData: IJackpotTicketState, jackpots: IJackpot[]) {
    stateData.jackpots = [...jackpots];
  },
  UPDATE_JACKPOTS(stateData: IJackpotTicketState, jackpots: IJackpot[]) {
    stateData.jackpots = unionBy(jackpots, stateData.jackpots, 'id');
  },
};

export const actions = {
  resetOwnerLuckyDrawTicket(context: any): void {
    context.commit('RESET_OWNER_LUCKY_DRAW_TICKETS');
  },
  resetOwnerPastTicket(context: any): void {
    context.commit('INSERT_OWNER_PAST_TICKETS');
  },
  resetOwnerInstantPrizeTicket(context: any): void {
    context.commit('INSERT_OWNER_INSTANT_PRIZE_TICKETS');
  },
  resetPastWinnes(context: any): void {
    context.commit('RESET_PAST_WINNERS');
  },
  getJackpots(context: any): void {
    if (!context.getters.isJackpotEnabled) return;
    
    const { rootGetters } = context;
    const token = rootGetters['general/token'];

    const currentJackpot = getData(API_URLS.JACKPOTS, token, { status: 1 });
    const pastJackpot = getData(API_URLS.JACKPOTS, token, { status: 4 });
    Promise.all([currentJackpot, pastJackpot])
      .then(([res1, res2]) => {
        const res = [...res1, ...res2];
        context.commit('INSERT_JACKPOTS', keysToCamel(res));
      })
      .catch((e) => console.log(e));
  },
  getProgressAmount(context: any): void {
    if (!context.getters.isJackpotEnabled) return;
    
    const { rootGetters } = context;
    const token = rootGetters['general/token'];

    getData(API_URLS.JACKPOTS, token, { status: 1 })
      .then((res) => {
        context.commit('UPDATE_JACKPOTS', keysToCamel(res));
      })
      .catch((e) => console.log(e));
  },
  getOwnerTickets(
    context: any,
    { id, type, isSeen = true, sort = 'desc' }: { id: string; type: string; isSeen: boolean; sort: string }
  ): void {
    if (!context.getters.isJackpotEnabled) return;

    const { rootGetters } = context;
    const token = rootGetters['general/token'];
    const params = {
      page: 1,
      page_size: 20,
      is_seen: isSeen,
      order_by: sort,
    };
    if (id) {
      params['jackpot_id'] = id;
    }

    context.commit('UPDATE_LOADING', true);
    getData(API_URLS.JACKPOT_MEMBER_TICKETS, token, params)
      .then((res) => {
        switch (type) {
          case 'current-lucky-draw':
            context.commit('INSERT_OWNER_CURRENT_TICKETS', keysToCamel(res));
            break;
          case 'past-lucky-draw':
            context.commit('INSERT_OWNER_PAST_TICKETS', keysToCamel(res));
            break;
          default:
            context.commit('INSERT_OWNER_TICKETS', keysToCamel(res));
        }
      })
      .catch((e) => console.log(e))
      .finally(() => context.commit('UPDATE_LOADING', false));
  },
  getOwnerTicketsLog(context: any): void {
    if (!context.getters.isJackpotEnabled) return;
    
    const { state: stateData, rootGetters } = context;
    const token = rootGetters['general/token'];
    const { ticketPagination } = stateData;
    const params = {
      page: ticketPagination.page,
      page_size: ticketPagination.pageSize,
      order_by: 'desc',
    };
    context.commit('UPDATE_LOADING', true);
    getData(API_URLS.JACKPOT_MEMBER_TICKETS, token, params)
      .then((res) => {
        if (ticketPagination.page === 1) {
          context.commit('INSERT_OWNER_TICKETS_LOG', keysToCamel(res));
        } else {
          context.commit('UPDATE_OWNER_TICKETS_LOG', keysToCamel(res));
        }
        context.commit('UPDATE_PAGINATION', {
          page: ticketPagination.page + 1,
          pageSize: ticketPagination.pageSize,
        });
      })
      .catch((e) => console.log(e))
      .finally(() => context.commit('UPDATE_LOADING', false));
  },
  resetTicketsPage(context: any): void {
    context.commit('RESET_PAGINATION');
  },
  getPastWinners(context: any): void {
    if (!context.getters.isJackpotEnabled) return;
    
    const { rootGetters } = context;
    const token = rootGetters['general/token'];
    const params = {
      page: 1,
      page_size: 12,
    };

    context.commit('UPDATE_LOADING', true);
    getData(API_URLS.JACKPOT_LUCKY_DRAWS_WINNERS, token, params)
      .then((res) => {
        context.commit('INSERT_PAST_WINNERS', keysToCamel(sortBy(res, 'rank')));
      })
      .catch((e) => console.log(e))
      .finally(() => context.commit('UPDATE_LOADING', false));
  },
  updateTicketsSeenStatus(context: any, id: string): Promise<void> {
    if (!context.getters.isJackpotEnabled) return Promise.resolve();
    
    const { rootGetters } = context;
    const token = rootGetters['general/token'];

    return postData(`${API_URLS.JACKPOT_MEMBER_TICKETS}${id}/seen`, token);
  },
  getAvailableTickets(context: any, id: string): void {
    if (!context.getters.isJackpotEnabled) return;
    
    const { rootGetters } = context;
    const token = rootGetters['general/token'];

    getData(`${API_URLS.JACKPOTS_TICKETS}?jackpot_id=${id}`, token)
      .then((res) => {
        context.commit('INSERT_LUCKY_DRAW_TICKETS', keysToCamel(res));
      })
      .catch((e) => console.log(e));
  },
};

export const getters = {
  isJackpotEnabled: (state: any, getters: any, rootState: any, rootGetters: any): boolean => {
    return rootGetters['general/config'].enableJackpot;
  },

  ticketLoading: (stateData: any): boolean => {
    return stateData.ticketLoading;
  },
  pastWinners: (stateData: any): IJackpotPastWinner[] => {
    return stateData.pastWinners;
  },
  currentLuckyDraw: (stateData: any): IJackpot[] => {
    const activeLuckyDraw = filter(
      stateData.jackpots,
      (jackpot) => jackpot.type === 0 && (jackpot.status === 1 || jackpot.status === 3)
    );
    return !isEmpty(activeLuckyDraw) ? activeLuckyDraw[0] : ({} as IJackpot);
  },
  lastLuckyDraw: (stateData: any): IJackpot[] => {
    const activeLuckyDraw = filter(stateData.jackpots, (jackpot) => jackpot.type === 0 && jackpot.status === 4);
    return !isEmpty(activeLuckyDraw) ? activeLuckyDraw[0] : ({} as IJackpot);
  },
  currentInstantPrize: (stateData: any): IJackpot[] => {
    const activeInstantPrize = filter(
      stateData.jackpots,
      (jackpot) => jackpot.type === 1 && (jackpot.status === 1 || jackpot.status === 3)
    );
    return !isEmpty(activeInstantPrize) ? activeInstantPrize[0] : ({} as IJackpot);
  },
  ownerLuckyDrawTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.ownerTickets.filter((t: IJackpotTicket) => t.jackpot?.type === 0);
  },
  ownerInstantPrizeTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.ownerTickets.filter((t: IJackpotTicket) => t.jackpot?.type === 1);
  },
  ownerTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.ownerTickets;
  },
  ownerTicketsLog: (stateData: any): IJackpotTicket[] => {
    return stateData.ownerTicketsLog;
  },
  curentOwnerLuckyDrawTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.ownnerCurrentLuckyDrawTickets;
  },
  pastOwnerTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.pastOwnerTickets;
  },
  currentLuckyDrawTickets: (stateData: any): IJackpotTicket[] => {
    return stateData.currentLuckyDrawTickets;
  },
  ticketPagination: (stateData: any): IPagination => {
    return stateData.ticketPagination;
  },
  currentLuckyDrawAmountDetail: (stateData: any, getter: any): { amount: number; unit: number } => {
    const { prevDateTime, nextDateTime, prevAmount, nextAmount } = getter.currentLuckyDraw;
    const timeIntervalInTenSec = mathFloor(
      (new Date(nextDateTime).getTime() - new Date(prevDateTime).getTime()) / 10000
    );
    const amountInterval = Number(nextAmount) - Number(prevAmount);
    const tenSecAmountUnit = amountInterval / timeIntervalInTenSec;

    const currentTimeDiff = mathFloor((new Date().getTime() - new Date(prevDateTime).getTime()) / 10000);
    const currentAmount = mathFloor((prevAmount + tenSecAmountUnit * currentTimeDiff) * 100) / 100;
    return {
      amount: currentAmount || 0,
      unit: mathFloor(tenSecAmountUnit * 100) / 100 || 0,
    };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
